import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";

import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import FilterDropdown from "designV2/components/FilterDropdown";
import StatusBadge from "designV2/components/StatusBadge";
import { endPoint } from "utils/config";
import { readableError } from "./peopleFields";
import "./superPeople.css";

const EDIT_USER = gql`
  mutation updateUserByAdmin(
    $userId: ID!
    $fullName: String
    $email: String!
    $phoneNumber: String!
    $isVerified: Boolean
    $emailVerified: Boolean
    $phoneNumberVerified: Boolean
    $birthDate: Date
    $gender: UserGender
    $state: UserStateInput
  ) {
    updateUserByAdmin(
      userId: $userId
      input: {
        fullName: $fullName
        isVerified: $isVerified
        emailVerified: $emailVerified
        phoneNumberVerified: $phoneNumberVerified
        email: $email
        phoneNumber: $phoneNumber
        birthDate: $birthDate
        gender: $gender
        state: $state
      }
    ) {
      _id
      fullName
      state
    }
  }
`;

const GENDER_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
];
const GENDER_LABELS = GENDER_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const STATE_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
];
const STATE_LABELS = STATE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const toInputDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");

const Toggle = ({ label, hint, checked, onChange }) => (
  <label className={`dsOptionCard ${checked ? "isSelected" : ""}`}>
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="dsOptionCardText">
      <strong>{label}</strong>
      <span>{hint}</span>
    </span>
  </label>
);

/**
 * EditUserForm - edit one customer.
 *
 * `email` and `phoneNumber` are non-null on the mutation, so the form
 * requires them rather than letting the server reject the save. The
 * verification flags are deliberately presented as what they are —
 * manual overrides — since setting them here skips the code the customer
 * would otherwise have to receive.
 */
const EditUserForm = ({ user }) => {
  const [fullName, setFullName] = useState(user.fullName || "");
  const [email, setEmail] = useState(user.email || "");
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber || "");
  const [gender, setGender] = useState(user.gender || "");
  const [state, setState] = useState(user.state || "ACTIVE");
  const [birthDate, setBirthDate] = useState(toInputDate(user.birthDate));
  const [isVerified, setIsVerified] = useState(Boolean(user.isVerified));
  const [emailVerified, setEmailVerified] = useState(Boolean(user.emailVerified));
  const [phoneNumberVerified, setPhoneNumberVerified] = useState(
    Boolean(user.phoneNumberVerified)
  );

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [updateUser] = useMutation(EDIT_USER);

  const validate = () => {
    const next = {};
    if (!fullName.trim()) next.fullName = "A name is required.";
    if (!email.trim()) next.email = "An email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(email.trim())) next.email = "That does not look like an email.";
    if (!phoneNumber.trim()) next.phoneNumber = "A phone number is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (saving) return;
    setFormError("");
    if (!validate()) return;

    setSaving(true);
    updateUser({
      variables: {
        userId: user._id,
        fullName: fullName.trim(),
        email: email.trim(),
        phoneNumber: phoneNumber.trim(),
        gender: gender || undefined,
        state,
        birthDate: birthDate || undefined,
        isVerified,
        emailVerified,
        phoneNumberVerified,
      },
    })
      .then(() => {
        window.location.href = `${endPoint}/superAdmin/customers`;
      })
      .catch((err) => {
        setSaving(false);
        setFormError(readableError(err));
      });
  };

  return (
    <div className="page superPeoplePage">
      <main className="main">
        <PageHeader
          title="Edit customer"
          description={user.fullName || ""}
          actions={
            <StatusBadge
              status={state === "SUSPENDED" ? "danger" : "success"}
              label={STATE_LABELS[state] || state}
            />
          }
        />

        <form onSubmit={onSubmit}>
          <Panel title="Details">
            <div className="superPersonForm">
              {formError && (
                <div className="dsFormMessage error" role="alert">
                  {formError}
                </div>
              )}

              <Field label="Full name" htmlFor="fullName" error={errors.fullName} required>
                <input
                  id="fullName"
                  className="dsInput"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </Field>

              <div className="superPersonFormRow">
                <Field label="Email" htmlFor="email" error={errors.email} required>
                  <input
                    id="email"
                    className="dsInput"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field>

                <Field
                  label="Phone number"
                  htmlFor="phoneNumber"
                  error={errors.phoneNumber}
                  required
                >
                  <input
                    id="phoneNumber"
                    className="dsInput"
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </Field>
              </div>

              <div className="superPersonFormRow">
                <Field label="Date of birth" htmlFor="birthDate" optional>
                  <input
                    id="birthDate"
                    className="dsInput"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                </Field>

                <Field label="Gender" optional>
                  <FilterDropdown
                    label={gender ? GENDER_LABELS[gender] : "Not set"}
                    options={[{ value: "", label: "Not set" }, ...GENDER_OPTIONS]}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  />
                </Field>
              </div>

              <Field
                label="Account state"
                hint="Suspending removes the customer's access to the app."
              >
                <FilterDropdown
                  label={STATE_LABELS[state] || state}
                  options={STATE_OPTIONS}
                  value={state}
                  active={false}
                  onChange={(e) => setState(e.target.value)}
                />
              </Field>
            </div>
          </Panel>

          <Panel
            title="Verification overrides"
            subtitle="Turning these on marks the customer verified without them receiving a code."
          >
            <div className="superPersonForm">
              <Toggle
                label="Phone verified"
                hint="Lets the customer sign in without confirming their number."
                checked={phoneNumberVerified}
                onChange={() => setPhoneNumberVerified((current) => !current)}
              />
              <Toggle
                label="Email verified"
                hint="Skips the confirmation email."
                checked={emailVerified}
                onChange={() => setEmailVerified((current) => !current)}
              />
              <Toggle
                label="Identity verified"
                hint="Marks the account as fully verified."
                checked={isVerified}
                onChange={() => setIsVerified((current) => !current)}
              />
            </div>
          </Panel>

          <div className="superPersonFooter">
            <Button
              variant="secondary"
              onClick={() => (window.location.href = `${endPoint}/superAdmin/customers`)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving}>
              Save changes
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default EditUserForm;
